import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { FileText, ChevronRight, Sparkles, Eye, X, GraduationCap, Calendar, Coins, Building2, Paperclip } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const STATUS_COLOR = {
  'Lead': 'bg-gray-100 text-gray-700',
  'Application Started': 'bg-blue-100 text-blue-700',
  'Documents Pending': 'bg-amber-100 text-amber-700',
  'Documents Completed': 'bg-emerald-100 text-emerald-700',
  'Submitted to University': 'bg-indigo-100 text-indigo-700',
  'Offer Received': 'bg-purple-100 text-purple-700',
  'Deposit Paid': 'bg-pink-100 text-pink-700',
  'Enrolled': 'bg-emerald-200 text-emerald-800',
  'Closed': 'bg-gray-200 text-gray-600',
};

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtBytes = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-gray-100 last:border-0">
    <Icon className="w-4 h-4 text-[#F5A623] mt-0.5 shrink-0" />
    <div className="min-w-0 flex-1">
      <div className="text-[11px] uppercase tracking-wider font-bold text-gray-500">{label}</div>
      <div className="text-sm font-medium text-[#1A2A4F] break-words">{value || '—'}</div>
    </div>
  </div>
);

const MyApplicationsPage = () => {
  const { api } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api().get('/student/me/applications');
        setApps(Array.isArray(r.data) ? r.data : []);
      } catch { /* silent */ }
      setLoading(false);
    };
    load();
  }, [api]);

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  };
  const sorted = [...apps].sort((a, b) => {
    const av = a[sort.key] ?? a.metadata?.[sort.key] ?? '';
    const bv = b[sort.key] ?? b.metadata?.[sort.key] ?? '';
    const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
    return sort.dir === 'asc' ? cmp : -cmp;
  });

  const renderTh = (k, label) => (
    <th onClick={() => toggleSort(k)}
      className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-bold text-[#1A2A4F]/70 cursor-pointer select-none"
      data-testid={`th-${k}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="text-gray-300">{sort.key === k ? (sort.dir === 'asc' ? '▲' : '▼') : ''}</span>
      </span>
    </th>
  );

  return (
    <div className="space-y-5" data-testid="my-applications-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center">
          <FileText className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#1A2A4F]">Your Applications</h1>
          <p className="text-sm text-gray-600">Track every application in one place.</p>
        </div>
      </div>

      {loading ? (
        <p className="text-center py-10 text-gray-500" data-testid="apps-loading">Loading applications…</p>
      ) : apps.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-10 text-center" data-testid="apps-empty">
          <div className="mx-auto w-14 h-14 rounded-full bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center">
            <Sparkles className="w-6 h-6" />
          </div>
          <h3 className="mt-4 font-bold text-[#1A2A4F]">No applications yet</h3>
          <p className="mt-2 text-sm text-gray-600 max-w-md mx-auto">
            Pick a course you love and tap Start Application to begin your journey.
          </p>
          <Link to="/courses"
            className="mt-5 inline-flex items-center gap-2 bg-[#F5A623] text-white font-bold px-5 py-2.5 rounded-md hover:brightness-95"
            data-testid="apps-browse-courses">
            Browse Programs <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden" data-testid="apps-table-wrap">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="apps-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-bold text-[#1A2A4F]/70 w-12">#</th>
                  {renderTh('program_name', 'Program')}
                  {renderTh('university_name', 'University')}
                  {renderTh('status', 'Status')}
                  {renderTh('intake', 'Intake')}
                  {renderTh('created_at', 'Created')}
                  {renderTh('updated_at', 'Updated')}
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sorted.map((a, i) => {
                  const m = a.metadata || {};
                  const program = a.program_name || a.course_title || m.course_title || m.program_name || '—';
                  const uni = a.university_name || m.university_name || '—';
                  const status = a.status || 'Lead';
                  const tone = STATUS_COLOR[status] || 'bg-gray-100 text-gray-700';
                  const intake = a.intake || m.intake || m.session_start || '—';
                  return (
                    <tr key={a.id || i} className="hover:bg-gray-50" data-testid={`app-tr-${a.id || i}`}>
                      <td className="px-4 py-3 font-mono text-gray-400">{i + 1}</td>
                      <td className="px-4 py-3 font-semibold text-[#1A2A4F]">{program}</td>
                      <td className="px-4 py-3 text-gray-700">{uni}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${tone}`} data-testid={`app-status-${a.id || i}`}>
                          {status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{intake}</td>
                      <td className="px-4 py-3 text-gray-600">{fmt(a.created_at)}</td>
                      <td className="px-4 py-3 text-gray-600">{fmt(a.updated_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setSelected(a)}
                          className="inline-flex items-center gap-1 text-[#F5A623] font-semibold text-xs hover:underline"
                          data-testid={`app-view-${a.id || i}`}
                        >
                          <Eye className="w-3.5 h-3.5" /> View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Application detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="app-detail-dialog">
          {selected && (() => {
            const a = selected;
            const m = a.metadata || {};
            const status = a.status || 'Lead';
            const tone = STATUS_COLOR[status] || 'bg-gray-100 text-gray-700';
            const program = a.program_name || a.course_title || m.course_title || m.program_name || '—';
            const uni = a.university_name || m.university_name || '—';
            const docs = Array.isArray(a.documents) ? a.documents : [];
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="text-[#1A2A4F] text-xl">{program}</DialogTitle>
                  <DialogDescription className="flex items-center gap-2">
                    <Building2 className="w-3.5 h-3.5 text-[#F5A623]" />
                    <span>{uni}</span>
                    <span className={`ml-auto text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${tone}`} data-testid="dlg-status">
                      {status}
                    </span>
                  </DialogDescription>
                </DialogHeader>

                <div className="mt-4 bg-gray-50 border border-gray-100 rounded-xl px-4 py-2">
                  <Row icon={GraduationCap} label="Degree" value={m.degree_type || m.degree || '—'} />
                  <Row icon={Calendar} label="Intake" value={a.intake || m.intake || m.session_start} />
                  <Row icon={Coins} label="Tuition" value={m.annual_tuition_fees_raw || (m.annual_tuition_fees != null ? String(m.annual_tuition_fees) : '—')} />
                  <Row icon={Calendar} label="Submitted on" value={fmt(a.created_at)} />
                  <Row icon={Calendar} label="Last updated" value={fmt(a.updated_at)} />
                  {a.assigned_to_name && <Row icon={Building2} label="Assigned to" value={a.assigned_to_name} />}
                </div>

                <div className="mt-4">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5">
                    <Paperclip className="w-3.5 h-3.5" /> Uploaded documents ({docs.length})
                  </h4>
                  {docs.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">No documents were attached during submission.</p>
                  ) : (
                    <ul className="space-y-1.5" data-testid="dlg-documents">
                      {docs.map((d, idx) => (
                        <li
                          key={d.id || idx}
                          className="flex items-center justify-between gap-2 px-3 py-2 bg-white border border-gray-100 rounded-lg text-sm"
                          data-testid={`dlg-doc-${idx}`}
                        >
                          <div className="min-w-0">
                            <div className="font-semibold text-[#1A2A4F] truncate">{d.caption || d.filename}</div>
                            <div className="text-[11px] text-gray-500 truncate">{d.filename} {d.size ? `· ${fmtBytes(d.size)}` : ''}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {a.notes && (
                  <div className="mt-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Notes</h4>
                    <p className="text-sm text-gray-700 whitespace-pre-line">{a.notes}</p>
                  </div>
                )}

                <DialogFooter className="mt-5">
                  <Button variant="outline" onClick={() => setSelected(null)} data-testid="dlg-close-btn">
                    <X className="w-4 h-4 mr-1.5" /> Close
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyApplicationsPage;
